import axios from "axios";
import { useState, useRef, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";

// ── Icons ──
const CalIcon = () => (
  <svg viewBox="0 0 16 16" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
    <rect x="1.5" y="2.5" width="13" height="12" rx="1.5"/>
    <path d="M5 1v3M11 1v3M1.5 7h13"/>
  </svg>
);

const BackIcon = () => (
  <svg viewBox="0 0 16 16" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
    <path d="M10 3L5 8l5 5"/>
  </svg>
);
const ShareIcon = () => (
  <svg viewBox="0 0 16 16" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
    <circle cx="12" cy="3" r="2"/><circle cx="4" cy="8" r="2"/><circle cx="12" cy="13" r="2"/>
    <path d="M6 7l4-3M6 9l4 3"/>
  </svg>
);


// ── Progress bar ──
function ReadingProgress() {
  const [progress, setProgress] = useState(0);
  useEffect(() => {
    const update = () => {
      const el = document.documentElement;
      const scrolled = el.scrollTop;
      const total = el.scrollHeight - el.clientHeight;
      setProgress(total > 0 ? (scrolled / total) * 100 : 0);
    };
    window.addEventListener("scroll", update);
    return () => window.removeEventListener("scroll", update);
  }, []);
  return (
    <div className="fixed top-0 left-0 right-0 z-50 h-0.5" style={{ background: "#e0ead8" }}>
      <div className="h-full transition-all duration-100"
        style={{ width: `${progress}%`, background: "linear-gradient(to right, #6ee85a, #3dcc40)" }} />
    </div>
  );
}

// ── Main Blog Details Page ──
export default function BlogDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [copied, setCopied] = useState(false);
  const [blog, setBlog] = useState(null);
  const [loading, setLoading] = useState(false);
  const [activeSection, setActiveSection] = useState(0);
  const sectionRefs = useRef([]);


    const getblogDetails = async () => {
  try {
    setLoading(true);
    const res = await axios.get(`${process.env.REACT_APP_API_URL}/blog/get-by-id/${id}`);

    console.log("data", res.data);

    setBlog(res.data); // 
  } catch (error) {
    console.log("Error ", error);
  } finally {
    setLoading(false);
  }
};

    useEffect(()=>{
        getblogDetails();
    },[]);

  // Track active section on scroll
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            const index = sectionRefs.current.indexOf(entry.target);
            if (index !== -1) setActiveSection(index);
          }
        });
      },
      { threshold: 0.4, rootMargin: "-80px 0px -40% 0px" }
    );
    sectionRefs.current.forEach(ref => { if (ref) observer.observe(ref); });
    return () => observer.disconnect();
  }, []);

  const handleShare = () => {
    navigator.clipboard?.writeText(window.location.href);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (!blog) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: "#f8faf6" }}>
        <div className="text-center">
          <p className="text-5xl mb-4">📝</p>
          <p className="font-black text-gray-700 text-xl mb-2">Article not found</p>
          <button onClick={() => navigate("/user-blog")}
            className="text-sm font-bold px-5 py-2.5 rounded-xl mt-3"
            style={{ background: "linear-gradient(135deg, #6ee85a, #3dcc40)", color: "#0d2d0d" }}>
            Back to Blog
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ background: "#f8faf6", fontFamily: "'Barlow', sans-serif" }}>
      <ReadingProgress />

      {/* ── Hero ── */}
      <div className="relative overflow-hidden" style={{ maxHeight: "480px" }}>
        <img src={blog.image} alt={blog.title} className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0"
          style={{ background: "linear-gradient(to bottom, rgba(8,20,8,0.55) 0%, rgba(8,20,8,0.85) 60%, rgba(8,20,8,0.97) 100%)" }} />

        <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-8 pt-10 pb-14">
          {/* Back + breadcrumb */}
          <div className="flex items-center gap-3 mb-8">
            <button onClick={() => navigate("/user-blog")}
              className="flex items-center gap-1.5 text-xs font-semibold px-3 py-2 rounded-xl transition-all hover:bg-white/20"
              style={{ background: "rgba(255,255,255,0.1)", color: "white", border: "1px solid rgba(255,255,255,0.15)" }}>
              <BackIcon /> Blog
            </button>
            <span className="text-green-600 text-xs">/</span>
            <span className="text-green-400 text-xs font-medium">{blog.author}</span>
          </div>

          {/* Tag */}
          {/* <span className="inline-flex items-center gap-1.5 text-xs font-bold px-3 py-1.5 rounded-full mb-4"
            style={{ background: tagStyle.bg, color: tagStyle.text }}>
            <TagIcon /> {blog.tag}
          </span> */}

          {/* Title */}
          <h1 className="text-white font-black leading-tight mb-5"
            style={{ fontSize: "clamp(22px, 4vw, 42px)", letterSpacing: "-0.02em" }}>
            {blog.title}
          </h1>

          {/* Meta row */}
          <div className="flex flex-wrap items-center gap-4 mb-6">
            <div className="flex items-center gap-2.5">
              <div className="w-9 h-9 rounded-full flex items-center justify-center text-sm font-black flex-shrink-0"
                style={{ background: blog.author.bg, color: blog.author.color }}>
                {blog.author.initials}
              </div>
              <div>
                <p className="text-white text-sm font-bold leading-none">{blog.author.name}</p>
                <p className="text-green-400 text-xs mt-0.5">{blog.author.role}</p>
              </div>
            </div>
            <div className="flex items-center gap-3 text-green-300 text-xs flex-wrap">
              <span className="flex items-center gap-1"><CalIcon /> {new Date(blog.updatedAt).toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" })}</span>
              <span style={{ color: "#3dcc40" }}>·</span>
            </div>
          </div>

          {/* Share */}
          <button onClick={handleShare}
            className="flex items-center gap-2 text-xs font-bold px-4 py-2.5 rounded-xl transition-all"
            style={{ background: copied ? "#EAF3DE" : "rgba(255,255,255,0.12)", color: copied ? "#3B6D11" : "white", border: "1px solid rgba(255,255,255,0.2)" }}>
            <ShareIcon /> {copied ? "Link copied!" : "Share article"}
          </button>
        </div>
      </div>

      {/* ── Body ── */}
      <div className="max-w-6xl mx-auto px-4 sm:px-8 py-10">
        <div className="flex gap-8 items-start">

      <div
  className="prose max-w-none text-gray-700"
  dangerouslySetInnerHTML={{ __html: blog.content }}
/>

          {/* ── Sidebar ── */}
          <aside className="hidden lg:flex flex-col gap-5 w-64 xl:w-72 flex-shrink-0 sticky top-6">
        
            {/* CTA */}
            <div className="rounded-2xl p-5 text-center"
              style={{ background: "linear-gradient(135deg, #0d2d0d, #1a4a1a)", border: "1px solid rgba(74,222,128,0.2)" }}>
              <p className="text-green-400 text-xs font-bold tracking-widest uppercase mb-2">Free Consultation</p>
              <p className="text-white font-black text-sm mb-1">Ready to go solar?</p>
              <p className="text-green-300 text-xs leading-relaxed mb-4">Get a free site assessment and ROI report from our experts.</p>
              <button
                onClick={() => navigate("/contact")}
                className="w-full py-2.5 rounded-xl font-black text-xs transition-all hover:opacity-90"
                style={{ background: "linear-gradient(135deg, #6ee85a, #3dcc40)", color: "#0d2d0d" }}>
                Get Free Quote →
              </button>
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
}